import React from 'react';
import { EditableField } from './EditableField';
import { Link, Lock } from 'lucide-react';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  jobContactEntryId?: number; // Link back to timeline item for billing tracking
}

interface InvoiceLineItemsProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
}

export const InvoiceLineItems: React.FC<InvoiceLineItemsProps> = ({
  items,
  onChange
}) => {
  const addNewRow = () => {
    const newItem: LineItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0
    };
    onChange([...items, newItem]);
  };

  const removeRow = (id: string) => {
    onChange(items.filter(item => item.id !== id));
  };

  const updateItem = (id: string, field: keyof LineItem, value: string | number) => {
    onChange(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const calculateAmount = (quantity: number, rate: number) => {
    return quantity * rate;
  };

  return (
    <div className="space-y-4">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-4 py-2 border-b border-gray-200 font-semibold text-gray-700">
        <div className="col-span-6">Item Description</div>
        <div className="col-span-2 text-center">Qty</div>
        <div className="col-span-2 text-right">Rate</div>
        <div className="col-span-2 text-right">Amount</div>
      </div>

      {/* Table Rows */}
      {items.map((item, index) => (
        <div key={item.id} className="grid grid-cols-12 gap-4 py-2 border-b border-gray-100">
          <div className="col-span-6">
            <div className="flex items-center space-x-2">
              <EditableField
                value={item.description}
                onChange={(value) => updateItem(item.id, 'description', value)}
                placeholder="Enter item description"
                className="flex-1"
              />
              {item.jobContactEntryId && (
                <div className="flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400" title={`Linked to timeline item #${item.jobContactEntryId}`}>
                  <Link className="w-3 h-3" />
                  <span>Timeline</span>
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <EditableField
              value={item.quantity.toString()}
              onChange={(value) => {
                const num = parseFloat(value) || 0;
                updateItem(item.id, 'quantity', num);
              }}
              placeholder="1"
              className="w-full text-center"
            />
          </div>
          <div className="col-span-2">
            <EditableField
              value={item.rate.toFixed(2)}
              onChange={(value) => {
                const num = parseFloat(value) || 0;
                updateItem(item.id, 'rate', num);
              }}
              placeholder="0.00"
              className="w-full text-right"
            />
          </div>
          <div className="col-span-2 flex items-center justify-end">
            <span className="text-right font-medium flex-1">
              ${calculateAmount(item.quantity, item.rate).toFixed(2)}
            </span>
            {items.length > 1 ? (
              <button
                onClick={() => removeRow(item.id)}
                className="ml-2 hover:scale-125 transition-transform duration-200"
                style={{
                  background: 'red',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}
                title="Remove Row"
                aria-label="Remove Row"
              >
                X
              </button>
            ) : (
              <div className="ml-2" style={{ width: '20px', flexShrink: 0 }}></div>
            )}
          </div>
        </div>
      ))}

      {/* Add Row Button */}
      <button
        onClick={addNewRow}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors"
      >
        + Add new row
      </button>
    </div>
  );
};
